import React, { FC, ReactNode, useCallback, useEffect, useState } from 'react'

import { FormattedMessage } from 'react-intl'
import { useRouteMatch } from 'react-router'

import { noop } from 'common/constants'
import {
  metaphorSlideContacts,
  metaphorSlideExtendedSearch,
  metaphorSlideFavourite,
  metaphorSlideInvisibleMan,
  metaphorSlideShowHitlistRating,
  metaphorSlideStickers,
} from 'common-constants/metaphor'
import { PromoCases, SearchItemPromo } from 'common-constants/promoCases'
import {
  ListItemPromoRouterLink,
  PromoButtonWrapper,
  PromoLayout,
  PromoText,
} from 'components/page/Search/Search.styled'
import {
  albumPath,
  uploadPhotosPath,
} from 'components/page/UploaderPhotos/Uploader.paths'
import {
  featuredPhotosPath,
  storeFrontPath,
  photolineBasePath,
} from 'components/paths'
import { Button } from 'components/presentational/button'
import { AddFavouriteIconSvg } from 'components/presentational/svg/AddFavouriteIconSvg'
import { ExtendsSearchParamsIconSvg } from 'components/presentational/svg/ExtendsSearchParamsIconSvg'
import { InvisibleManSvg } from 'components/presentational/svg/InvisibleManSvg'
import { NewContactsIconSvg } from 'components/presentational/svg/NewContactsIconSvg'
import { PhotoCamera } from 'components/presentational/svg/PhotoCamera'
import { Photoline2Svg } from 'components/presentational/svg/Photoline2Svg'
import { Rating2Svg } from 'components/presentational/svg/Rating2Svg'
import { StickersIcon2Svg } from 'components/presentational/svg/StickersIcon2Svg'
import { TopUp2Svg } from 'components/presentational/svg/TopUp2Svg'
import {
  storeFrontProfileUpPath,
  storeFrontVipPath,
} from 'components/storefront/StoreFront.paths'
import { mergeAllUrls } from 'functions/mergeAllUrls'

import { FeaturedPhotoButtonTextSwitch } from './FeaturedPhotoIconSwitch/FeaturedPhotoButtonTextSwitch'
import { FeaturedPhotoIconSwitch } from './FeaturedPhotoIconSwitch/FeaturedPhotoIconSwitch'
import { FeaturedPhotoTextSwitch } from './FeaturedPhotoIconSwitch/FeaturedPhotoTextSwitch'
import { VipLimitsText } from './VipLimitsText'

interface PresetData {
  Icon: ReactNode
  text: string
  buttonText: string
  uniwebPath: string
}

const getServicePreset = (itemCase): PresetData => {
  let Icon
  let text
  let buttonText

  let uniwebPath

  switch (itemCase) {
    case PromoCases.COMMON.TOPUP:
      Icon = <TopUp2Svg width="100%" />
      text = <FormattedMessage id="storefront.legend.top_profile" />
      buttonText = <FormattedMessage id="storefront.profile_up.title" />
      uniwebPath = storeFrontProfileUpPath
      break

    case PromoCases.COMMON.FEATURED:
      Icon = <FeaturedPhotoIconSwitch />
      text = <FeaturedPhotoTextSwitch />
      buttonText = <FeaturedPhotoButtonTextSwitch />
      uniwebPath = featuredPhotosPath
      break

    case PromoCases.COMMON.PHOTOLINE:
      Icon = <Photoline2Svg width="100%" />
      text = <FormattedMessage id="storefront.legend.photo" />
      buttonText = <FormattedMessage id="storefront.button.publish" />
      uniwebPath = `${storeFrontPath}/${photolineBasePath}`
      break

    case PromoCases.UPLOAD:
      Icon = <PhotoCamera width="100%" />
      text = <FormattedMessage id="hitlist.promo.add_photo.description" />
      buttonText = <FormattedMessage id="hitlist.promo.add_photo.title" />
      uniwebPath = `${uploadPhotosPath}/${albumPath}`
      break

    case PromoCases.VIP.FAVORITES:
      Icon = <AddFavouriteIconSvg width="100%" />
      text = <FormattedMessage id="storefront.legend.favourite_who" />
      buttonText = <FormattedMessage id="settings.activate_vip_short" />
      uniwebPath = `${storeFrontVipPath}/${metaphorSlideFavourite}`
      break

    case PromoCases.VIP.INCOGNITO:
      Icon = <InvisibleManSvg width="100%" />
      text = <FormattedMessage id="storefront.legend.invisible" />
      buttonText = <FormattedMessage id="settings.activate_vip_short" />
      uniwebPath = `${storeFrontVipPath}/${metaphorSlideInvisibleMan}`
      break

    case PromoCases.VIP.LIKES:
      Icon = <Rating2Svg width="100%" />
      text = <FormattedMessage id="storefront.legend.hitlist" />
      buttonText = <FormattedMessage id="settings.activate_vip_short" />
      uniwebPath = `${storeFrontVipPath}/${metaphorSlideShowHitlistRating}`
      break

    case PromoCases.VIP.LIMITS:
      Icon = <NewContactsIconSvg width="100%" />
      text = <VipLimitsText />
      buttonText = <FormattedMessage id="settings.activate_vip_short" />
      uniwebPath = `${storeFrontVipPath}/${metaphorSlideContacts}`
      break

    case PromoCases.VIP.SEARCH:
      Icon = <ExtendsSearchParamsIconSvg width="100%" />
      text = <FormattedMessage id="storefront.legend.extended_search" />
      buttonText = <FormattedMessage id="settings.activate_vip_short" />
      uniwebPath = `${storeFrontVipPath}/${metaphorSlideExtendedSearch}`
      break

    case PromoCases.VIP.STICKERS:
      Icon = <StickersIcon2Svg width="100%" />
      text = <FormattedMessage id="storefront.legend.stickers" />
      buttonText = <FormattedMessage id="settings.activate_vip_short" />
      uniwebPath = `${storeFrontVipPath}/${metaphorSlideStickers}`
      break

    default:
      throw new Error('Unknown type for service preset')
  }

  return {
    Icon,
    text,
    buttonText,
    uniwebPath,
  }
}

export const SearchListPromoLegacy: FC<{
  item: SearchItemPromo
  getNearElement: () => HTMLDivElement
}> = ({ item, getNearElement }) => {
  const match = useRouteMatch()
  const [nearElementHeight, setNearElementHeight] = useState(0)
  const [preset, setPreset] = useState<PresetData | null>(null)

  const updateElementHeight = useCallback(() => {
    requestAnimationFrame(() => {
      const nearElement = getNearElement()

      if (nearElement) {
        setNearElementHeight(nearElement.clientHeight)
      }
    })
  }, [getNearElement])

  const setupPreset = useCallback(() => {
    const preset = getServicePreset(item.case)

    setPreset(preset)
  }, [item.case])

  useEffect(() => {
    updateElementHeight()
    setupPreset()
    window.addEventListener('resize', updateElementHeight)

    return () => {
      window.removeEventListener('resize', updateElementHeight)
    }
  }, [setupPreset, updateElementHeight])

  if (preset === null) {
    return null
  }

  const promoHeight =
    nearElementHeight > 0 ? `${nearElementHeight}px` : undefined

  return (
    <ListItemPromoRouterLink to={mergeAllUrls(match.url, preset.uniwebPath)}>
      <PromoLayout style={{ height: promoHeight }}>
        {preset.Icon}
        <PromoText>{preset.text}</PromoText>
        <PromoButtonWrapper data-name={`item-${item.case}-button`}>
          <Button onClick={noop} data-name="button-payment-success">
            {preset.buttonText}
          </Button>
        </PromoButtonWrapper>
      </PromoLayout>
    </ListItemPromoRouterLink>
  )
}
